import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FlexRow, Link, SvgIconSocial } from '@make-software/cspr-ui';

import { AccountInfoOwner } from '@block-explorer/entities';
import {
  AccountInfoAccordion,
  AccountInfoTable,
} from '../account-info-accordion/account-info-accordion';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsNameTableData from '../details-name-table-data/details-name-table-data';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import { DetailsLocation } from '../details-location/details-location';
import { BodyText } from '@block-explorer/ui';

const SpanSeparator = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: 28,
}));

export const DetailsAccordionGeneralInfo = ({
  accordionTitle,
  owner,
}: {
  accordionTitle: React.ReactElement;
  owner?: AccountInfoOwner;
}) => {
  const { t } = useTranslation();

  const ownerEmpty = owner == null || Object.keys(owner).length === 0;
  const website = owner?.website;

  return ownerEmpty ? null : (
    <AccountInfoAccordion
      disableClickAway
      renderContent={() => (
        <AccountInfoTable
          renderData={() => (
            <>
              {website && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Website</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    <Link color="primaryBlue" href={website}>
                      <BodyText size={3} scale={'sm'}>
                        {website}
                      </BodyText>
                    </Link>
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {owner?.email && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Email</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    <Link color="primaryBlue" href={'mailto:' + owner?.email}>
                      {owner?.email}
                    </Link>
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {owner?.description && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Description</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    {owner?.description}
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {owner?.location?.country && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Location</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    <DetailsLocation location={owner?.location} />
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {(owner?.type?.length || null) && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Type</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData
                    bodyTextProps={{ capitalize: true, size: 3 }}
                  >
                    {owner?.type?.join(', ')}
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {owner?.identity &&
                Object.values(owner.identity).some((i) => i) && (
                  <DetailsTableRow>
                    <DetailsNameTableData>
                      <Trans t={t}>Identity</Trans>
                    </DetailsNameTableData>
                    <DetailsValueTableData>
                      {owner?.identity.ownership_disclosure_url && (
                        <>
                          <Link
                            color="primaryBlue"
                            href={owner?.identity.ownership_disclosure_url}
                          >
                            {t('Ownership Disclosure')}
                          </Link>
                          <SpanSeparator />
                        </>
                      )}
                      {owner?.identity.casper_association_kyc_url && (
                        <>
                          <Link
                            color="primaryBlue"
                            href={owner?.identity.casper_association_kyc_url}
                          >
                            {t('Casper Association KYC')}
                          </Link>
                          <SpanSeparator />
                        </>
                      )}
                      {owner?.identity.casper_association_kyc_onchain && (
                        <>
                          <Link
                            color="primaryBlue"
                            href={
                              owner?.identity.casper_association_kyc_onchain
                            }
                          >
                            {t('Casper association KYC-onchain')}
                          </Link>
                          <SpanSeparator />
                        </>
                      )}
                    </DetailsValueTableData>
                  </DetailsTableRow>
                )}
              {owner?.resources &&
                Object.values(owner.resources).some((i) => i) && (
                  <DetailsTableRow>
                    <DetailsNameTableData>
                      <Trans t={t}>Policy resources</Trans>
                    </DetailsNameTableData>
                    <DetailsValueTableData>
                      {owner?.resources.code_of_conduct_url && (
                        <>
                          <Link
                            color="primaryBlue"
                            href={owner?.resources.code_of_conduct_url}
                          >
                            {t('Code of conduct')}
                          </Link>
                          <SpanSeparator />
                        </>
                      )}
                      {owner?.resources.terms_of_service_url && (
                        <>
                          <Link
                            color="primaryBlue"
                            href={owner?.resources.terms_of_service_url}
                          >
                            {t('Terms of service')}
                          </Link>
                          <SpanSeparator />
                        </>
                      )}
                      {owner?.resources.privacy_policy_url && (
                        <Link
                          color="primaryBlue"
                          href={owner?.resources.privacy_policy_url}
                        >
                          {t('Privacy policy')}
                        </Link>
                      )}
                    </DetailsValueTableData>
                  </DetailsTableRow>
                )}
              {(owner?.resources?.other?.length || null) && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Other</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    {owner?.resources?.other?.map((i) => [
                      <Link
                        key={i.name + 'link'}
                        color="primaryBlue"
                        href={i.url}
                      >
                        {i.name}
                      </Link>,
                      <SpanSeparator key={i.name + 'sep'} />,
                    ])}
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
              {owner?.social && Object.values(owner?.social).some((i) => i) && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Social media</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData>
                    <FlexRow align="center">
                      {Object.entries(owner?.social)
                        .filter(([key, value]) => Boolean(value))
                        .map(([key, value]: any) => [
                          <SvgIconSocial
                            key={key + 'icon'}
                            socialMediaType={
                              key === 'twitter' ? 'twitter_x' : (key as any)
                            }
                            userId={value}
                          />,
                          <SpanSeparator key={key + 'sep'} />,
                        ])}
                    </FlexRow>
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}
            </>
          )}
        />
      )}
    >
      {accordionTitle}
    </AccountInfoAccordion>
  );
};
